import React, { useEffect, useState } from "react";
import api, { downloadFile } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Download } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["new", "contacted", "follow_up", "converted", "lost"];
const STAGE_COLORS = { new: "bg-blue-100 text-blue-700", contacted: "bg-amber-100 text-amber-700", follow_up: "bg-purple-100 text-purple-700", converted: "bg-green-100 text-green-700", lost: "bg-red-100 text-red-700" };

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = () => { const params = status !== "all" ? { status } : {}; api.get("/admin/leads", { params }).then((r) => setLeads(r.data)); };
  useEffect(load, [status]);

  const update = async (id, patch) => { await api.put(`/admin/leads/${id}`, patch); toast.success("Lead updated"); load(); };
  const addNote = async () => { if (!note.trim() || !selected) return; await api.put(`/admin/leads/${selected.id}`, { note }); setNote(""); toast.success("Note added"); const r = await api.get("/admin/leads"); const fresh = r.data.find((l) => l.id === selected.id); setSelected(fresh); setLeads(r.data); };

  const exportXlsx = async () => {
    try { await downloadFile("/admin/leads/export/xlsx", "leads.xlsx"); }
    catch { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif text-3xl text-navy">CRM / Leads</h1>
        <button onClick={exportXlsx} data-testid="export-leads-btn" className="flex items-center gap-2 bg-navy text-cream text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-navy-soft transition-colors"><Download size={15} /> Export Excel</button>
      </div>
      <div className="flex gap-2 mb-5 flex-wrap">
        <button onClick={() => setStatus("all")} className={`px-4 py-2 rounded-full text-sm ${status === "all" ? "bg-navy text-cream" : "bg-white border border-black/10"}`}>All</button>
        {STAGES.map((s) => <button key={s} onClick={() => setStatus(s)} data-testid={`lead-filter-${s}`} className={`px-4 py-2 rounded-full text-sm capitalize ${status === s ? "bg-navy text-cream" : "bg-white border border-black/10"}`}>{s.replace(/_/g, " ")}</button>)}
      </div>
      <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
        <table className="w-full min-w-[620px] text-sm">
          <thead className="bg-beige text-navy text-left"><tr><th className="p-3">Name</th><th className="p-3">Contact</th><th className="p-3">Source</th><th className="p-3">Stage</th><th className="p-3"></th></tr></thead>
          <tbody>
            {leads.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No leads captured yet</td></tr> :
              leads.map((l) => (
                <tr key={l.id} className="border-t border-black/5 hover:bg-beige/40" data-testid={`lead-${l.id}`}>
                  <td className="p-3 font-medium text-navy">{l.name}</td>
                  <td className="p-3"><div>{l.phone}</div><div className="text-xs text-muted-foreground">{l.email}</div></td>
                  <td className="p-3"><span className="text-xs bg-beige px-2 py-1 rounded">{l.source}</span></td>
                  <td className="p-3">
                    <Select value={l.status} onValueChange={(v) => update(l.id, { status: v })}>
                      <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`lead-stage-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s} className="capitalize text-xs">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3"><button onClick={() => setSelected(l)} className="text-xs font-semibold text-navy hover:text-gold">View</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md">
          {selected && (<>
            <DialogHeader><DialogTitle className="font-serif text-2xl">{selected.name}</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <div className="text-muted-foreground">{selected.phone} · {selected.email}</div>
              <span className={`inline-block text-xs px-2 py-1 rounded-full capitalize ${STAGE_COLORS[selected.status]}`}>{selected.status.replace(/_/g, " ")}</span>
              {selected.requirement && <div><b>Requirement:</b> {selected.requirement}</div>}
              {selected.event_type && <div><b>Event:</b> {selected.event_type} · {selected.guests || "?"} guests · {selected.event_date || ""}</div>}
              {selected.message && <div><b>Message:</b> {selected.message}</div>}
              {selected.budget && <div><b>Budget:</b> {selected.budget}</div>}
              <div className="pt-2">
                <div className="font-semibold text-navy mb-1">Notes</div>
                {(selected.notes || []).map((n, i) => <div key={i} className="text-xs bg-beige rounded p-2 mb-1">{n.text}</div>)}
                <div className="flex gap-2 mt-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note..." data-testid="lead-note-input" className="flex-1 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
                  <button onClick={addNote} data-testid="add-note-btn" className="bg-navy text-cream text-sm px-4 rounded-lg">Add</button>
                </div>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
